/* eslint-disable jsx-a11y/anchor-is-valid */

/**
 * Libs
 */
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import React, { useEffect } from 'react';

/**
 * Constants
 */
import { RouteE } from 'src/constants/pages.constants';
import { CollectionCardBig } from 'src/features/common/collection/components/collection-card-big';
import { CollectionCardBigPreloader } from 'src/features/common/collection/components/collection-card-big-preloader';
import { EmptyListPlaceholder } from 'src/features/common/shared/components/empty-list-placeholder';

/**
 * Hooks
 */
import { useFetchLatestCollections } from 'src/features/common/universe/hooks/use-fetch-latest-collections';
import { useUniverse } from 'src/features/common/universe/hooks/use-universe';
import { useScreenDetect } from 'src/hooks/useScreenDetect';
import { Button } from 'src/ui/common/atoms/button';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { Grid } from 'src/ui/common/atoms/grid';

/**
 * Components
 */
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Typography } from 'src/ui/common/atoms/typography';
import { Visible } from 'src/ui/common/layouts/visible';

export const LatestCollections: React.VFC<{ title?: string }> = ({ title }) => {
    const { t } = useTranslation(['pages', 'components']);
    const { isUniverseOwner } = useUniverse();
    const screen = useScreenDetect();
    const { loading, collections, fetchCollections } = useFetchLatestCollections();

    const hasCollections = !!collections.length;

    useEffect(() => {
        fetchCollections();
    }, [fetchCollections]);

    if (!hasCollections && !isUniverseOwner) {
        return null;
    }

    return (
        <Spacing padding="80px 0 0">
            <Spacing margin="0 0 30px">
                <FlexBox alignItems="center" justifyContent="space-between">
                    <Typography variant="title" size={screen.xs || screen.sm ? 'base' : 'md'} color="white">
                        {title || t('home.widgets.latestCollections.title')}
                    </Typography>
                    {hasCollections && !loading && (
                        <Visible view={['laptop', 'desktop']}>
                            <Link href={RouteE.COLLECTIONS_LIST} prefetch={false}>
                                <a>
                                    <Button variant="outline">{t('home.seeMore')}</Button>
                                </a>
                            </Link>
                        </Visible>
                    )}
                </FlexBox>
            </Spacing>
            {!hasCollections ? (
                <EmptyListPlaceholder itemsGridConfig={{ xs: 1, sm: 3, md: 3, lg: 3, xl: 3, columnGap: 20 }}>
                    <FlexBox flexDirection="column" alignItems="center">
                        <Spacing margin="0 0 30px">
                            <Typography size="base" variant="title" color="white">
                                {t('emptyList.collection.collectionsLatest.text', { ns: 'components' })}
                            </Typography>
                        </Spacing>
                        <Link href={RouteE.COLLECTION_CREATE}>
                            <a>
                                <Button variant="white">
                                    {t('emptyList.collection.collectionsLatest.create', { ns: 'components' })}
                                </Button>
                            </a>
                        </Link>
                    </FlexBox>
                </EmptyListPlaceholder>
            ) : (
                <>
                    <Spacing margin="0 0 30px">
                        <Grid xs={1} sm={3} columnGap={20} rowGap={20}>
                            {loading &&
                                [...Array.from({ length: 3 }).keys()].map((idx) => (
                                    <CollectionCardBigPreloader key={idx} />
                                ))}
                            {collections.map((collection) => (
                                <CollectionCardBig key={collection.id} collection={collection} />
                            ))}
                        </Grid>
                    </Spacing>
                    {hasCollections && !loading && (
                        <Visible view={['mobile']}>
                            <Link href={RouteE.COLLECTIONS_LIST}>
                                <a>
                                    <Button variant="outline" stretch>
                                        {t('home.seeMore')}
                                    </Button>
                                </a>
                            </Link>
                        </Visible>
                    )}
                </>
            )}
        </Spacing>
    );
};
